import BaseAPIClient from './base/BaseAPIClient';
import AppointmentRequestModel from '../models/AppointmentRequestModel';
import AppointmentModel from '../models/AppointmentModel';
import ListResponseModel from '../models/ListResponseModel';
import CounselorGroupSessionModel from '../models/CounselorGroupSessionModel';
import AvailabilityPeriodModel from '../models/AvailabilityPeriodModel';
import PatientModel from '../models/PatientModel';
import CounselorAvailabilityModel from '../models/CounselorAvailabilityModel.js';

export default class CounselorClient extends BaseAPIClient {
  getMyAppointmentRequests(limit = 10, offset = 0) {
    return this._get('counseling_sessions/my_appointment_requests/', {
      pending: true,
      limit,
      offset,
    }).then(m => new ListResponseModel(m, AppointmentRequestModel));
  }

  getMyAppointmentsChart() {
    return this._get('counselors/me/appointments_report/');
  }

  getWorkingHours(id) {
    return this._get(`counselors/counselors/${id}/working_hours/`);
  }

  getUpcomingAppointments(limit = 10, offset = 0) {

    const params = {
      approved: true,
      end_time_after: new Date().toISOString(),
      limit,
      offset,
      ordering: 'start_time',
    };

    return this._get('counseling_sessions/counselors/my_appointments/', params).then(m => new ListResponseModel(m, AppointmentModel));
  }

  getMyAppointments(filters, limit = 10, offset = 0, sortColumn = 'start_time', sortAsc = false) {

    const params = {
      approved: true,
      limit,
      offset
    };

    params.ordering = `${(sortAsc ? "" : "-")}${sortColumn}`

    if (filters && filters.start_time_after) {
      params.start_time_after = filters.start_time_after;
    }

    if (filters && filters.start_time_before) {
      params.start_time_before = filters.start_time_before;
    }

    return this._get('counseling_sessions/counselors/my_appointments/', params).then(m => new ListResponseModel(m, AppointmentModel));
  }

  scheduleAppointmentForStudent(user_id, start_time, end_time) {
    return this._post('counseling_sessions/counselors/appointments/schedule/', {
      user_id,
      start_time,
      end_time,
    });
  }

  reScheduleAppointmentForCounselor(appointment_id, start_time, end_time) {
    return this._post('counseling_sessions/counselors/appointments/reschedule/', {
      appointment_id,
      start_time,
      end_time,
    });
  }

  getMyPatients(limit = 10, offset = 0) {
    return this._get('counselors/me/counselor_profile/patients/', {
      limit,
      offset,
    }).then(m => new ListResponseModel(m, PatientModel));
  }

  respondToConselingRequest(request_id, approved, response_explanation) {
    return this._patch(`counseling_sessions/my_appointment_requests/${request_id}/`, {
      approved,
      response_explanation,
    });
  }


  // cronofy
  completeCalendarAuth(code, redirect_uri) {
    return this._post('counselors/me/complete_calendar_authorization/', {
      origin: document.location.origin,
      code,
      redirect_uri,
    });
  }

  syncCalendar() {
    return this._post('counselors/me/calendar_sync/', {
      origin: document.location.origin,
    });
  }

  syncCalendarAvailability() {
    return this._post('counselors/me/availability_rules/', {
      origin: document.location.origin,
    });
  }

  addNewCalendar() {
    return this._post('counselors/me/initialize_application_calendar/');
  }


  getCalendarAvailabilityForPreview(counselor_id) {
    return this._post(`counselors/counselors/${counselor_id}/availability/`, {
      origin: document.location.origin,
    });
  }

  createGroupSession(data) {
    return this._post('counseling_sessions/counselors/group_sessions/', data).then(res => new CounselorGroupSessionModel(res));
  }

  getGroupSessions(filters, limit = 10, offset = 0) {
    const data = filters || {};
    data.type = 'group_session';
    data.limit = limit;
    data.offset = offset;
    data.ordering = 'start_time';

    return this._get('counseling_sessions/counselors/group_sessions/', data).then(m => new ListResponseModel(m, CounselorGroupSessionModel));
  }

  getGroupSessionParticipants(id) {
    return this._get(`counseling_sessions/counselors/group_sessions/${id}/attendees/`).then(res => {
      return res.active_attendees.map(a => {
        return new PatientModel(a.user);
      });
    }).catch(ex => {
      return [];
    });
  }

  deleteSession(id) {
    return this._delete(`counseling_sessions/counselors/group_sessions/${id}/`);
  }

  // Group Events Workflows
  createGroupEvent(data) {
    return this._post('counseling_sessions/counselors/group_sessions/', {
      type: 'college_event',
      ...data,
    }).then(res => new CounselorGroupSessionModel(res));
  }

  getGroupEvents(filters, limit = 10, offset = 0) {
    const data = filters || {};
    data.type = 'college_event';
    data.limit = limit;
    data.offset = offset;

    return this._get('counseling_sessions/counselors/group_sessions/', data).then(m => new ListResponseModel(m, CounselorGroupSessionModel));
  }

  deleteEvent(id) {
    return this._delete(`counseling_sessions/counselors/group_sessions/${id}/`, {
      type: 'college_event',
    });
  }



  // add availability custom table
  addAvailabilityPeriod(start, end) {
    return this._post('counselors/me/available_periods/', {
      start,
      end,
    }).then(res => new AvailabilityPeriodModel(res));
  }

  getMyAvailabilityPeriods(limit = 10, offset = 0) {
    return this._get('counselors/me/available_periods/', {
      limit,
      offset,
    }).then(m => new ListResponseModel(m, AvailabilityPeriodModel));
  }

  editAvailabilityPeriod(id, start, end) {
    return this._put(`counselors/me/available_periods/${id}/`, {
      start,
      end,
    }).then(m => new ListResponseModel(m, AvailabilityPeriodModel));
  }

  deleteAvailabilityPeriod(id) {
    return this._delete(`counselors/me/available_periods/${id}/`);
  }

  getUnbufferedAvailabilitySlots(counselor_id, week) {
    return this._post(`counselors/unbuffered_availability`, {counselor_id, week})
      .then(m => (m.days || []).map(m => new CounselorAvailabilityModel(m)));
  }

  updatedAvailability(counselorId) {
    return this._post(`v2/counselors/availability-updated/`, {counselorId})
  }

  requestLicenseApproval(
    counselorId,  // : Number
    type,         // : string
    licenseNumber,// : string
    usStateId,    // : Number
    expires)      // : moment.Moment
  {
    expires = expires.toISOString();

    return this._post(`v2/counselors/license/request-approval`, {
      counselorId,
      type,
      licenseNumber,
      usStateId,
      expires
    })
  }
  getCounselorDocuments(counselorId) {
    return this._get(`v2/counselors/documents`, {
      counselorId
    })
  }

  approveLicense(licenseId) {
    return this._post(`v2/counselors/license/approve`, {
      licenseId
    })
  }

  rejectLicense(licenseId) {
    return this._post(`v2/counselors/license/reject`, {
      licenseId
    })
  }
  
  removeLicense(licenseId) {
    return this._post(`v2/counselors/license/remove`, {
      licenseId
    })
  }

  unlinkCalendar(counselorId) {
    return this._post('v2/counselors/unlink-calendar/', { counselorId });
  }

  getPatientDetail(counselorId, studentUserId) {
    return this._get('v2/counselors/patients/detail/', { counselorId, studentUserId });
  }

  getCounselorClients(counselorId, searchCriteria, pageToken) {
    return this._get('v2/counselors/clients/', {
      counselorId,
      searchCriteria,
      pageToken,
    });
  }

  getSessionPrice(counselorId) {
    return this._get(`counselors/counselors/${counselorId}/session_price/`);
  }

  getAvailabilityToken(counselorId) {
    return this._get('v2/counselors/element-token/availability/', {
      counselorId,
      origin: document.location.origin
    });
  }

  getManagedAvailabilityToken(counselorId) {
    return this._get('v2/counselors/element-token/managed_availability/', {
      counselorId,
      origin: document.location.origin
    });
  }

  getAgendaToken(counselorId) {
    return this._get('v2/counselors/element-token/agenda/', {
      counselorId,
      origin: document.location.origin
    });
  }

  getAccountManagementToken(counselorId) {
    return this._get('v2/counselors/element-token/account_management/', {
      counselorId,
      origin: document.location.origin
    });
  }

  requestNewWorkshop(data) {
    return this._post('v2/workshops/request/', data);
  }

  getCalendarEvents(counselorId, start, end) {
    return this._get('v2/counselors/calendar/events/', {
      counselorId,
      start,
      end
    })
  }
}


// POST “Complete 3rd party calendar authorization”. Takes in code and redirect_uri and completes the Cronofy authorization.
// POST “Initialize cronofy application calendar”
// POST “Get calendar sync parameters”
// POST “Get manage availability parameters”
// POST “Get availability parameters”
// POST “Request appointment”. Pass in counselor_id, start_time, end_time.




// https://docs.google.com/document/d/1FzgE_aEeukxmzdhl5Clrom2iD2MiEi8GAenmqYYfRJ4/edit#
// Auth Flow
// completeCalendarAuth
// Initialize cronofy application calendar?
// syncCalendar
// syncCalendarAvailability
// setCalendarAvailability

// // POST “Request appointment”. Pass in counselor_id, start_time, end_time.
